import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Film, Clapperboard, Loader2, Send, Trash2, RotateCcw, AlertTriangle, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, daysUntilJob } from "@/utils/constants";

const GRID = "sm:grid sm:grid-cols-[32px_1fr_100px_140px_100px_60px_130px] sm:gap-2";
const DOC_GRID = "sm:grid sm:grid-cols-[32px_1fr_150px_1fr_130px] sm:gap-2";

const weeksSinceWedding = (dateStr) => {
  const days = daysUntilJob(dateStr);
  if (days === null || days > 0) return null;
  return Math.floor(-days / 7);
};

const weeksSinceDate = (dateStr) => {
  if (!dateStr) return null;
  const days = daysUntilJob(dateStr);
  if (days === null) return null;
  return Math.max(0, Math.floor(-days / 7));
};

const mondayOf = (dateStr) => {
  const d = new Date(dateStr);
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day);
  return d.toISOString().slice(0, 10);
};

const niceDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
const shortDate = (dateStr) => dateStr ? new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

const buildClogBanners = (entries) => {
  const dateCounts = {};
  const weekCounts = {};
  entries.forEach((e) => {
    if (!e.wedding_date) return;
    dateCounts[e.wedding_date] = (dateCounts[e.wedding_date] || 0) + 1;
    const wk = mondayOf(e.wedding_date);
    weekCounts[wk] = (weekCounts[wk] || 0) + 1;
  });
  const lastDateIdx = {};
  const lastWeekIdx = {};
  entries.forEach((e, i) => {
    if (!e.wedding_date) return;
    lastDateIdx[e.wedding_date] = i;
    lastWeekIdx[mondayOf(e.wedding_date)] = i;
  });
  const banners = {};
  Object.entries(lastDateIdx).forEach(([date, idx]) => {
    if (dateCounts[date] >= 2) {
      banners[idx] = banners[idx] || [];
      banners[idx].push(`${dateCounts[date]} weddings captured on ${niceDate(date)} — possible editing clog here`);
    }
  });
  Object.entries(lastWeekIdx).forEach(([wk, idx]) => {
    if (weekCounts[wk] >= 4) {
      banners[idx] = banners[idx] || [];
      banners[idx].push(`${weekCounts[wk]} weddings in the week of ${niceDate(wk)} — possible editing clog here`);
    }
  });
  return banners;
};

const QueueRow = ({ entry, index, isTopThree, weeksLabel, weeksNum, onSaved, onSent, onDelete }) => {
  const [couple, setCouple] = useState(entry.couple_name);
  const [photographer, setPhotographer] = useState(entry.photographer || "");
  const [notes, setNotes] = useState(entry.notes || "");
  const [finish, setFinish] = useState(entry.proposed_finish || "");
  const [editingDate, setEditingDate] = useState(false);

  useEffect(() => {
    setCouple(entry.couple_name);
    setPhotographer(entry.photographer || "");
    setNotes(entry.notes || "");
    setFinish(entry.proposed_finish || "");
  }, [entry]);

  const saveField = async (field, value, original) => {
    if (value.trim() === (original || "").trim()) return;
    try {
      await axios.put(`${API}/editing-queue/${entry.id}`, { [field]: value });
      onSaved();
    } catch {
      toast.error("Failed to save");
    }
  };

  const inputCls = "h-7 text-sm border-transparent hover:border-gray-200 focus:border-gray-300 px-1.5";

  const weeksEl = entry.wedding_date ? (
    <span
      className={`text-xs text-center font-medium whitespace-nowrap ${weeksNum !== null && weeksNum >= 20 ? 'text-red-600' : weeksNum !== null && weeksNum >= 12 ? 'text-amber-600' : 'text-[#6b7280]'}`}
      data-testid={`queue-weeks-${entry.id}`}
      title={`Wedding: ${entry.wedding_date}`}
    >
      {weeksLabel}
    </span>
  ) : editingDate ? (
    <input
      type="date"
      autoFocus
      className="text-[10px] border border-gray-300 rounded px-0.5 h-6 w-[110px] sm:w-full"
      onBlur={() => setEditingDate(false)}
      onChange={async (e) => {
        if (!e.target.value) return;
        try {
          await axios.put(`${API}/editing-queue/${entry.id}`, { wedding_date: e.target.value });
          setEditingDate(false);
          toast.success("Wedding date saved");
          onSaved();
        } catch { toast.error("Failed to save date"); }
      }}
      data-testid={`queue-set-date-${entry.id}`}
    />
  ) : (
    <button
      onClick={() => setEditingDate(true)}
      className="text-xs text-center font-medium text-[#6b7280] underline decoration-dotted hover:text-[#1f2937] whitespace-nowrap"
      data-testid={`queue-weeks-${entry.id}`}
      title="No exact wedding date on record — click to set it"
    >
      {weeksLabel}
    </button>
  );

  return (
    <div
      className={`${GRID} sm:items-center px-2 py-1.5 rounded-md ${isTopThree ? 'bg-[#8DA399]/10 border border-[#8DA399]/40' : 'border border-gray-100 sm:border-transparent hover:bg-gray-50'}`}
      data-testid={`queue-row-${entry.id}`}
    >
      {/* Line 1 on mobile / first cells on desktop */}
      <span className={`hidden sm:block text-xs font-mono ${isTopThree ? 'text-[#3d5a47] font-bold' : 'text-[#8d8b8b]'}`}>{index + 1}</span>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className={`sm:hidden text-xs font-mono flex-shrink-0 ${isTopThree ? 'text-[#3d5a47] font-bold' : 'text-[#8d8b8b]'}`}>{index + 1}</span>
        <Input
          value={couple}
          onChange={(e) => setCouple(e.target.value)}
          onBlur={() => saveField('couple_name', couple, entry.couple_name)}
          className={`${inputCls} flex-1 font-medium sm:font-normal`}
          data-testid={`queue-couple-${entry.id}`}
        />
        {entry.photo_only && (
          <span className="flex-shrink-0 text-[9px] font-semibold uppercase tracking-wide bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-full" data-testid={`queue-photo-only-${entry.id}`}>
            Photo Only
          </span>
        )}
        {entry.highlight_film && (
          <span className="flex-shrink-0 text-[9px] font-semibold uppercase tracking-wide bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full" data-testid={`queue-highlight-${entry.id}`}>
            Highlight Film
          </span>
        )}
        {entry.video_only && !entry.highlight_film && (
          <span className="flex-shrink-0 text-[9px] font-semibold uppercase tracking-wide bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded-full" data-testid={`queue-video-only-${entry.id}`}>
            Video Only
          </span>
        )}
        <span className="sm:hidden flex-shrink-0">{weeksEl}</span>
      </div>
      {/* Line 2 on mobile / remaining cells on desktop */}
      <div className="contents max-sm:flex max-sm:items-center max-sm:gap-1.5 max-sm:mt-1">
        <Input
          value={photographer}
          onChange={(e) => setPhotographer(e.target.value)}
          onBlur={() => saveField('photographer', photographer, entry.photographer)}
          placeholder="Photographer"
          className={`${inputCls} max-sm:w-[86px] max-sm:border-gray-200 flex-shrink-0`}
          data-testid={`queue-photographer-${entry.id}`}
        />
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => saveField('notes', notes, entry.notes)}
          placeholder="Notes"
          className={`${inputCls} max-sm:flex-1 max-sm:border-gray-200 min-w-0`}
          data-testid={`queue-notes-${entry.id}`}
        />
        <Input
          value={finish}
          onChange={(e) => setFinish(e.target.value)}
          onBlur={() => saveField('proposed_finish', finish, entry.proposed_finish)}
          placeholder="Finish"
          className={`${inputCls} max-sm:w-[64px] max-sm:border-gray-200 flex-shrink-0`}
          data-testid={`queue-finish-${entry.id}`}
        />
        <span className="hidden sm:block">{weeksEl}</span>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          {isTopThree ? (
            <Button
              size="sm"
              onClick={() => onSent(entry.id)}
              className="h-6 text-[10px] px-2 bg-green-600 hover:bg-green-700 text-white ml-1"
              data-testid={`queue-sent-${entry.id}`}
            >
              <Send className="h-3 w-3 sm:mr-1" />
              <span className="hidden sm:inline">Mark as Sent</span>
            </Button>
          ) : (
            <span className="hidden sm:block w-[20px]" />
          )}
          <button
            onClick={() => { if (window.confirm(`Remove ${entry.couple_name} from the queue?`)) onDelete(entry.id); }}
            className="p-1 text-gray-300 hover:text-red-500"
            data-testid={`queue-delete-${entry.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

const DocRow = ({ entry, index, isTopThree, onSaved, onDone, onDelete }) => {
  const [couple, setCouple] = useState(entry.couple_name);
  const [notes, setNotes] = useState(entry.notes || "");

  useEffect(() => {
    setCouple(entry.couple_name);
    setNotes(entry.notes || "");
  }, [entry]);

  const saveField = async (field, value, original) => {
    if (value.trim() === (original || "").trim()) return;
    try {
      await axios.put(`${API}/documentary-queue/${entry.id}`, { [field]: value });
      onSaved();
    } catch {
      toast.error("Failed to save");
    }
  };

  const inputCls = "h-7 text-sm border-transparent hover:border-gray-200 focus:border-gray-300 px-1.5";
  const wks = weeksSinceDate(entry.order_date);
  const statusLabel = entry.production_status ? String(entry.production_status).replace(/_/g, ' ') : '';

  return (
    <div
      className={`${DOC_GRID} sm:items-center px-2 py-1.5 rounded-md ${isTopThree ? 'bg-[#8DA399]/10 border border-[#8DA399]/40' : 'border border-gray-100 sm:border-transparent hover:bg-gray-50'}`}
      data-testid={`doc-row-${entry.id}`}
    >
      <span className={`hidden sm:block text-xs font-mono ${isTopThree ? 'text-[#3d5a47] font-bold' : 'text-[#8d8b8b]'}`}>{index + 1}</span>
      <div className="flex items-center gap-1.5 min-w-0">
        <span className={`sm:hidden text-xs font-mono flex-shrink-0 ${isTopThree ? 'text-[#3d5a47] font-bold' : 'text-[#8d8b8b]'}`}>{index + 1}</span>
        <Input
          value={couple}
          onChange={(e) => setCouple(e.target.value)}
          onBlur={() => saveField('couple_name', couple, entry.couple_name)}
          className={`${inputCls} flex-1 font-medium sm:font-normal`}
          data-testid={`doc-couple-${entry.id}`}
        />
        {statusLabel && (
          <span
            className="flex-shrink-0 text-[9px] font-semibold uppercase tracking-wide bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full"
            data-testid={`doc-status-${entry.id}`}
            title="Production status from orders system"
          >
            {statusLabel}
          </span>
        )}
        {isTopThree && (
          <span className="flex-shrink-0 text-[9px] font-semibold uppercase tracking-wide bg-[#8DA399]/30 text-[#3d5a47] px-1.5 py-0.5 rounded-full">
            Next up
          </span>
        )}
      </div>
      <div className="contents max-sm:flex max-sm:items-center max-sm:gap-2 max-sm:mt-1">
        <div className="flex flex-col leading-tight max-sm:flex-shrink-0" data-testid={`doc-order-${entry.id}`}>
          <span className="text-xs text-[#1f2937] whitespace-nowrap">{shortDate(entry.order_date) || '—'}</span>
          {wks !== null && (
            <span className={`text-[10px] ${wks >= 8 ? 'text-amber-600' : 'text-[#8d8b8b]'}`}>
              {wks === 0 ? 'this week' : `${wks} wk${wks === 1 ? '' : 's'} ago`}
            </span>
          )}
        </div>
        <Input
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => saveField('notes', notes, entry.notes)}
          placeholder="Notes"
          className={`${inputCls} max-sm:flex-1 max-sm:border-gray-200 min-w-0`}
          data-testid={`doc-notes-${entry.id}`}
        />
        <div className="flex items-center gap-1 flex-shrink-0 justify-end">
          <Button
            size="sm"
            onClick={() => onDone(entry.id)}
            className="h-6 text-[10px] px-2 bg-green-600 hover:bg-green-700 text-white"
            data-testid={`doc-done-${entry.id}`}
          >
            <Check className="h-3 w-3 sm:mr-1" />
            <span className="hidden sm:inline">Mark Done</span>
          </Button>
          <button
            onClick={() => { if (window.confirm(`Remove ${entry.couple_name} from the documentary queue?`)) onDelete(entry.id); }}
            className="p-1 text-gray-300 hover:text-red-500"
            data-testid={`doc-delete-${entry.id}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export const EditingQueueDialog = ({ open, onClose }) => {
  const [mode, setMode] = useState('wedding'); // 'wedding' | 'documentary'
  const [entries, setEntries] = useState([]);
  const [sentEntries, setSentEntries] = useState([]);
  const [docEntries, setDocEntries] = useState([]);
  const [docDone, setDocDone] = useState([]);
  const [tab, setTab] = useState('queue');
  const [loading, setLoading] = useState(false);

  const fetchQueue = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const [active, sent] = await Promise.all([
        axios.get(`${API}/editing-queue`),
        axios.get(`${API}/editing-queue/sent`),
      ]);
      setEntries(active.data);
      setSentEntries(sent.data);
    } catch {
      toast.error("Failed to load editing queue");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDocQueue = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    try {
      const [active, done] = await Promise.all([
        axios.get(`${API}/documentary-queue`),
        axios.get(`${API}/documentary-queue/done`),
      ]);
      setDocEntries(active.data);
      setDocDone(done.data);
    } catch {
      toast.error("Failed to load documentary queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) { setMode('wedding'); setTab('queue'); fetchQueue(true); }
  }, [open, fetchQueue]);

  const switchMode = (next) => {
    setMode(next);
    setTab('queue');
    if (next === 'documentary') fetchDocQueue(true);
    else fetchQueue(true);
  };

  const handleSent = async (id) => {
    try {
      await axios.post(`${API}/editing-queue/${id}/sent`);
      toast.success("Marked as sent");
      fetchQueue();
    } catch { toast.error("Failed to mark as sent"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/editing-queue/${id}`);
      toast.success("Removed from queue");
      fetchQueue();
    } catch { toast.error("Failed to remove"); }
  };

  const handleRestore = async (id) => {
    try {
      await axios.post(`${API}/editing-queue/${id}/restore`);
      toast.success("Restored to queue");
      fetchQueue();
    } catch { toast.error("Failed to restore"); }
  };

  const handleDocDone = async (id) => {
    try {
      await axios.post(`${API}/documentary-queue/${id}/done`);
      toast.success("Marked done");
      fetchDocQueue();
    } catch { toast.error("Failed to mark done"); }
  };

  const handleDocDelete = async (id) => {
    try {
      await axios.delete(`${API}/documentary-queue/${id}`);
      toast.success("Removed from queue");
      fetchDocQueue();
    } catch { toast.error("Failed to remove"); }
  };

  const handleDocRestore = async (id) => {
    try {
      await axios.post(`${API}/documentary-queue/${id}/restore`);
      toast.success("Restored to queue");
      fetchDocQueue();
    } catch { toast.error("Failed to restore"); }
  };

  const isDoc = mode === 'documentary';
  const activeCount = isDoc ? docEntries.length : entries.length;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col" data-testid="editing-queue-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isDoc ? <Clapperboard className="h-5 w-5 text-[#605e5e]" /> : <Film className="h-5 w-5 text-[#605e5e]" />}
            {isDoc ? 'Documentary Edits' : 'Wedding Que'}
            <span className="text-xs bg-gray-100 text-[#605e5e] px-2 py-0.5 rounded-full font-normal">{activeCount} in queue</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Editing queue</DialogDescription>
        </DialogHeader>

        {/* Mode toggle: Wedding <-> Documentary */}
        <div className="flex items-center gap-1 p-1 rounded-full bg-gray-100 w-fit" data-testid="queue-mode-toggle">
          <button
            onClick={() => switchMode('wedding')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium transition-colors ${!isDoc ? 'bg-white text-[#1f2937] shadow-sm' : 'text-[#8d8b8b] hover:text-[#605e5e]'}`}
            data-testid="mode-wedding-btn"
          >
            <Film className="h-3.5 w-3.5" />
            Wedding Edits
          </button>
          <button
            onClick={() => switchMode('documentary')}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium transition-colors ${isDoc ? 'bg-white text-[#1f2937] shadow-sm' : 'text-[#8d8b8b] hover:text-[#605e5e]'}`}
            data-testid="mode-documentary-btn"
          >
            <Clapperboard className="h-3.5 w-3.5" />
            Documentary Edits
          </button>
        </div>

        <div className="flex gap-1 border-b border-[#e5e7eb] -mt-1">
          <button
            onClick={() => setTab('queue')}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'queue' ? 'border-[#605e5e] text-[#605e5e]' : 'border-transparent text-[#8d8b8b] hover:text-[#605e5e]'}`}
            data-testid="queue-tab"
          >
            Queue
          </button>
          <button
            onClick={() => setTab('sent')}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === 'sent' ? 'border-[#605e5e] text-[#605e5e]' : 'border-transparent text-[#8d8b8b] hover:text-[#605e5e]'}`}
            data-testid="sent-tab"
          >
            {isDoc
              ? `Recently Done${docDone.length > 0 ? ` (${docDone.length})` : ''}`
              : `Recently Sent${sentEntries.length > 0 ? ` (${sentEntries.length})` : ''}`}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#605e5e]" />
          </div>
        ) : isDoc ? (
          /* ===================== DOCUMENTARY MODE ===================== */
          tab === 'sent' ? (
            <div className="flex-1 overflow-y-auto -mx-2 px-2">
              <div className="space-y-0.5" data-testid="doc-done-list">
                {docDone.map((entry) => (
                  <div key={entry.id} className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-gray-50" data-testid={`doc-done-row-${entry.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1f2937]">{entry.couple_name}</p>
                      <p className="text-[11px] text-[#8d8b8b]">
                        Done {entry.done_at ? shortDate(entry.done_at) : ''}
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDocRestore(entry.id)}
                      className="h-7 text-xs border-[#8DA399] text-[#3d5a47] hover:bg-[#8DA399]/10"
                      data-testid={`doc-restore-${entry.id}`}
                    >
                      <RotateCcw className="h-3 w-3 mr-1" />
                      Restore
                    </Button>
                  </div>
                ))}
                {docDone.length === 0 && (
                  <p className="text-center text-sm text-[#8d8b8b] py-8">Nothing marked done yet</p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto -mx-2 px-2">
              <div className={`${DOC_GRID} hidden sm:grid px-2 pb-1 text-[10px] font-medium text-[#8d8b8b] uppercase tracking-wide sticky top-0 bg-white z-10`}>
                <span>#</span>
                <span>Couple</span>
                <span>Order Date</span>
                <span>Notes</span>
                <span></span>
              </div>
              <div className="space-y-1.5 sm:space-y-0.5" data-testid="doc-list">
                {docEntries.map((entry, index) => (
                  <DocRow
                    key={entry.id}
                    entry={entry}
                    index={index}
                    isTopThree={index < 3}
                    onSaved={() => fetchDocQueue()}
                    onDone={handleDocDone}
                    onDelete={handleDocDelete}
                  />
                ))}
                {docEntries.length === 0 && (
                  <p className="text-center text-sm text-[#8d8b8b] py-8">No documentary edits ordered yet</p>
                )}
              </div>
            </div>
          )
        ) : tab === 'sent' ? (
          /* ===================== WEDDING MODE ===================== */
          <div className="flex-1 overflow-y-auto -mx-2 px-2">
            <div className="space-y-0.5" data-testid="sent-list">
              {sentEntries.map((entry) => (
                <div key={entry.id} className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-gray-50" data-testid={`sent-row-${entry.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#1f2937]">{entry.couple_name}</p>
                    <p className="text-[11px] text-[#8d8b8b]">
                      {entry.photographer ? `${entry.photographer} · ` : ''}
                      Sent {entry.sent_at ? new Date(entry.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleRestore(entry.id)}
                    className="h-7 text-xs border-[#8DA399] text-[#3d5a47] hover:bg-[#8DA399]/10"
                    data-testid={`sent-restore-${entry.id}`}
                  >
                    <RotateCcw className="h-3 w-3 mr-1" />
                    Restore
                  </Button>
                </div>
              ))}
              {sentEntries.length === 0 && (
                <p className="text-center text-sm text-[#8d8b8b] py-8">Nothing marked as sent yet</p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto -mx-2 px-2">
            <div className={`${GRID} hidden px-2 pb-1 text-[10px] font-medium text-[#8d8b8b] uppercase tracking-wide sticky top-0 bg-white z-10`}>
              <span>#</span>
              <span>Couple</span>
              <span>Photographer</span>
              <span>Notes</span>
              <span>Proposed Finish</span>
              <span className="text-center">Weeks Since</span>
              <span></span>
            </div>
            <div className="space-y-1.5 sm:space-y-0.5" data-testid="queue-list">
              {(() => {
                const weeksValues = entries.map((e) => weeksSinceWedding(e.wedding_date));
                const clogBanners = buildClogBanners(entries);
                return entries.map((entry, index) => {
                  let weeksNum = weeksValues[index];
                  let weeksLabel;
                  if (weeksNum !== null) {
                    weeksLabel = `${weeksNum} wk${weeksNum === 1 ? '' : 's'}`;
                  } else {
                    const laterKnown = weeksValues.slice(index + 1).find((w) => w !== null);
                    if (laterKnown !== undefined && laterKnown !== null) {
                      weeksNum = laterKnown;
                      weeksLabel = `${laterKnown}+ wks`;
                    } else {
                      weeksLabel = '—';
                    }
                  }
                  return (
                    <React.Fragment key={entry.id}>
                      <QueueRow
                        entry={entry}
                        index={index}
                        isTopThree={index < 3}
                        weeksLabel={weeksLabel}
                        weeksNum={weeksNum}
                        onSaved={() => fetchQueue()}
                        onSent={handleSent}
                        onDelete={handleDelete}
                      />
                      {clogBanners[index] && clogBanners[index].map((text, bi) => (
                        <div
                          key={`clog-${index}-${bi}`}
                          className="flex items-center gap-2 px-3 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-medium"
                          data-testid={`clog-banner-${index}-${bi}`}
                        >
                          <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                          {text}
                        </div>
                      ))}
                    </React.Fragment>
                  );
                });
              })()}
              {entries.length === 0 && (
                <p className="text-center text-sm text-[#8d8b8b] py-8">Queue is empty</p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
